<script setup>
/* global axios */
import { computed, nextTick, ref, watch } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import {
  DropdownContainer,
  DropdownBody,
  DropdownItem,
} from 'next/dropdown-menu/base';

// Painel "Consultar IAra": chat separado dentro da caixa de resposta, nunca
// um 3º modo do toggle Responder/Mensagem Privada (risco real de vazar
// mensagem pro lead, ver project_chatwoot_fork_customizacao no SecondBrain).
// Histórico gravado em IaraChatMessage (ODFlow), isolado da conversa real.
// Chama o ODFlow via proxy do próprio Chatwoot (Api::V1::Accounts::
// IaraController), autenticado pela sessão do Chatwoot (sempre presente),
// nunca pela sessão do ODFlow (achado real de bug: agente sem sessão ativa
// no ODFlow no mesmo navegador via "Não autorizado").
const getters = useStoreGetters();
const currentChat = computed(() => getters.getSelectedChat.value);
const conversationId = computed(() => currentChat.value?.id);
const accountId = computed(() => getters.getCurrentAccountId.value);

const messages = ref([]);
const draft = ref('');
const isLoading = ref(false);
const isSending = ref(false);
const chatContainer = ref(null);
const modelOptions = ref([]);
const STORAGE_KEY = 'iara_consult_model';
const selectedModel = ref(localStorage.getItem(STORAGE_KEY) || '');
const isExpanded = ref(false);

watch(selectedModel, value => {
  if (value) localStorage.setItem(STORAGE_KEY, value);
});

async function scrollToBottom() {
  await nextTick();
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
  }
}

async function loadHistory(id) {
  if (!id) {
    messages.value = [];
    return;
  }
  isLoading.value = true;
  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/iara/history`,
      { params: { conversationId: id } }
    );
    messages.value = response.data.messages ?? [];
    modelOptions.value = response.data.modelOptions ?? [];
    if (!selectedModel.value && modelOptions.value.length) {
      [selectedModel.value] = modelOptions.value.map(o => o.value);
    }
  } catch {
    messages.value = [];
  } finally {
    isLoading.value = false;
    scrollToBottom();
  }
}

watch(conversationId, id => loadHistory(id), { immediate: true });

function modelLabel(value) {
  return modelOptions.value.find(o => o.value === value)?.label ?? value;
}

async function sendToIara({ displayText, message, period }) {
  if (!conversationId.value || isSending.value) return;

  messages.value.push({
    id: `local-${Date.now()}`,
    role: 'user',
    content: displayText,
  });
  isSending.value = true;
  scrollToBottom();

  try {
    const response = await axios.post(
      `/api/v1/accounts/${accountId.value}/iara/consult`,
      {
        conversationId: conversationId.value,
        message,
        model: selectedModel.value || undefined,
        period,
      }
    );
    messages.value.push({
      id: `local-${Date.now()}-r`,
      role: 'assistant',
      content: response.data.reply,
      model: response.data.model,
    });
  } catch (error) {
    messages.value.push({
      id: `local-${Date.now()}-r`,
      role: 'assistant',
      content: `Erro: ${error.response?.data?.error ?? 'não foi possível falar com a IAra agora.'}`,
    });
  } finally {
    isSending.value = false;
    scrollToBottom();
  }
}

async function sendMessage() {
  const text = draft.value.trim();
  if (!text) return;
  draft.value = '';
  await sendToIara({ displayText: text, message: text });
}

const PERIOD_LABELS = {
  '48h': 'Resumo das últimas 48h',
  '7d': 'Resumo dos últimos 7 dias',
  '30d': 'Resumo dos últimos 30 dias',
};

function triggerResumo(period) {
  sendToIara({
    displayText: PERIOD_LABELS[period] ?? 'Resumo',
    message: '/resumo',
    period,
  });
}
</script>

<template>
  <div
    class="flex flex-col text-sm transition-[height] duration-150"
    :class="isExpanded ? 'h-[520px]' : 'h-[280px]'"
  >
    <div class="flex items-center justify-between gap-1 px-1 pb-1.5 shrink-0">
      <select
        v-model="selectedModel"
        class="reset-base bg-n-green-9/5 border border-n-green-9/20 rounded-md text-xxs px-1.5 py-1 text-n-green-12 focus:outline-0 focus:ring-1 focus:ring-n-green-9"
      >
        <option v-for="opt in modelOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
      <div class="flex items-center gap-1 shrink-0">
        <DropdownContainer>
          <template #trigger="{ toggle }">
            <button
              type="button"
              class="reset-base text-xxs px-1.5 py-1 rounded-md border border-n-green-9/20 bg-n-green-9/5 text-n-green-12 hover:bg-n-green-9/15 transition-colors whitespace-nowrap"
              @click="toggle"
            >
              {{ 'Resumir 🤖' }}
            </button>
          </template>
          <DropdownBody class="ltr:right-0 rtl:left-0 min-w-40 z-30">
            <DropdownItem
              label="Últimas 48h"
              :click="() => triggerResumo('48h')"
            />
            <DropdownItem
              label="Últimos 7 dias"
              :click="() => triggerResumo('7d')"
            />
            <DropdownItem
              label="Últimos 30 dias"
              :click="() => triggerResumo('30d')"
            />
          </DropdownBody>
        </DropdownContainer>
        <button
          type="button"
          class="text-n-green-12/60 hover:text-n-green-12 transition-colors"
          :title="isExpanded ? 'Recolher' : 'Expandir'"
          @click="isExpanded = !isExpanded"
        >
          <span
            :class="isExpanded ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'"
          />
        </button>
      </div>
    </div>
    <div
      ref="chatContainer"
      class="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3 px-1 py-2"
    >
      <div v-if="isLoading" class="text-n-green-12/50">
        {{ 'Carregando conversa com a IAra...' }}
      </div>
      <div v-else-if="!messages.length" class="text-n-green-12/50">
        {{
          '🤖 Pergunte algo sobre este lead. Suas perguntas aqui não são enviadas ao cliente.'
        }}
      </div>
      <template v-else>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="flex flex-col gap-0.5"
          :class="msg.role === 'user' ? 'items-end' : 'items-start'"
        >
          <span class="text-xxs text-n-green-12/60">{{
            msg.role === 'user' ? 'Você' : 'IAra'
          }}</span>
          <div
            class="rounded-xl px-3 py-2 max-w-[85%] break-words whitespace-pre-wrap"
            :class="
              msg.role === 'user'
                ? 'bg-n-green-9/15 text-n-green-12 ltr:rounded-br-sm rtl:rounded-bl-sm'
                : 'bg-n-solid-green text-n-green-12 ltr:rounded-bl-sm rtl:rounded-br-sm'
            "
          >
            {{ msg.content }}
          </div>
          <span v-if="msg.model" class="text-xxs text-n-green-12/40">{{
            modelLabel(msg.model)
          }}</span>
        </div>
      </template>
      <div v-if="isSending" class="text-n-green-12/50">
        {{ 'IAra está pensando...' }}
      </div>
    </div>
    <form
      class="flex items-end gap-2 pt-2 shrink-0"
      @submit.prevent="sendMessage"
    >
      <textarea
        v-model="draft"
        rows="1"
        class="flex-1 reset-base bg-n-green-9/5 px-3 py-2 text-sm border border-n-green-9/20 rounded-lg focus:outline-0 focus:ring-2 focus:ring-n-green-9 resize-none text-n-green-12 placeholder:text-n-green-12/40"
        placeholder="Pergunte pra IAra sobre este lead..."
        @keydown.enter.exact.prevent="sendMessage"
      />
      <button
        type="submit"
        class="h-9 px-3 rounded-lg bg-n-green-9 text-white disabled:opacity-50"
        :disabled="isSending || !draft.trim()"
      >
        <span class="i-ph-arrow-up" />
      </button>
    </form>
  </div>
</template>
